<template>
    <Modal class="start-quick-modal"
           :loading="isLoading"
           v-on="listeners"
           v-bind="$props">
        <div slot="modal-header" class="modal-header">
            <div class="font-2 modal-title">
                Start Quick A/B Test
            </div>
            <AppHeader uniqueId="quickTestModalHeader"
                       :appIcon="appIcon"
                       :appName="appName"
                       class="app-header"
                       v-once
            >
                <template slot="subHeader">
                    <Platform :platform="platform.toLowerCase()" color="LIGHT_800"></Platform>
                    <span class="font-7 platform-text">
                        {{ platform }}
                    </span>
                </template>
            </AppHeader>
        </div>
        <div slot="modal-body">
            <div class="body">
                <SystemMessage v1
                               :message="errorMessage"
                               type="error"
                               class="create-error-message"
                               @acknowledged="errorMessage = ''"></SystemMessage>
                <div class="field">
                    <label for="abQuickName">A/B Test Name</label>
                    <input type="text"
                           id="abQuickName"
                           v-model="name"
                           :disabled="isLoading"
                           :maxlength="nameMaxLength"
                           placeholder="Enter your A/B test name"
                           :validation="$v.name">
                    <div class="error-message font-7"
                         v-if="$v.$anyError && !$v.name.required">{{nameMandatoryError}}</div>
                    <div class="error-message font-7"
                         v-else-if="$v.$anyError && !$v.name.minLength">{{nameMandatoryError}}</div>
                    <div class="error-message font-7"
                         v-else-if="$v.$anyError && !$v.name.maxLength">{{nameMaxLengthError}}</div>
                </div>
                <div class="field">
                    <label for="abQuickDescription">
                        Description <span class="font-5 optional">(Optional)</span>
                    </label>
                    <textarea id="abQuickDescription"
                              class="description"
                              v-model="description"
                              :disabled="isLoading"
                              maxlength="80"
                              placeholder="Enter your A/B test description"
                              :validation="$v.description" />
                    <div class="error-message font-7"
                         v-if="$v.$anyError && !$v.description.maxLength">{{descriptionMaxLengthError}}</div>
                </div>
                <div class="check font-5" v-once>
                    <IconBase iconName="checkV2Icon" height="8" width="10" :iconColor="consts.COLORS.LIGHT_900"/>
                    <span class="check-text">
                        Traffic will be divided 50/50, <span class="font-4">effective immediately</span>
                    </span>
                </div>
                <div class="check font-5" v-once>
                    <IconBase iconName="checkV2Icon" height="8" width="10" :iconColor="consts.COLORS.LIGHT_900"/>
                    <span class="check-text">
                        Update the settings for each group on the Mediation Management page
                    </span>
                </div>
            </div>
        </div>
    </Modal>
</template>

<script>
import Modal from 'components/modals/Modal.vue';
import validators from 'services/libWrappers/vuelidate/validators';
import AppHeader from 'components/appHeader/AppHeader.vue';
import http from 'services/utils/http';
import { testTypeQuick } from '@/pages/monetize/abTest/consts';
import { getAngularProvider, isAngular } from 'services/utils/angularUtils';
import SystemMessage from 'components/SystemMessage/SystemMessage.vue';
import IconBase from 'components/IconBase.vue';
import consts from 'services/metaData/consts';
import Platform from 'components/appHeader/Platform.vue';
import {
    MANDATORY_FIELD_ERROR,
    getErrorMessageForValue,
    MAXLENGTH_ERROR
} from 'services/metaData/errors';

const { required, minLength, maxLength } = validators;
const nameMaxLength = 30;

export default {
    name: 'StartQuickABModal',
    components: {
        Platform,
        IconBase,
        SystemMessage,
        AppHeader,
        Modal
    },
    props: {
        ...Modal.props,
        appIcon: String,
        platform: String,
        appName: String,
        appKey: String
    },
    data() {
        return {
            isLoading: false,
            errorMessage: null,
            name: null,
            description: null,
            consts,
            nameMaxLength,
            nameMandatoryError: getErrorMessageForValue(MANDATORY_FIELD_ERROR, 'A/B Test Name'),
            nameMaxLengthError: getErrorMessageForValue(MAXLENGTH_ERROR, nameMaxLength.toString()),
            descriptionMaxLengthError: getErrorMessageForValue(MAXLENGTH_ERROR, '80'),
        };
    },
    computed: {
        listeners() {
            // we bind the @ok listener to override it and emit an ok event with arguments (the component data)
            return { ...this.$listeners, ok: this.emitOk };
        }
    },
    methods: {
        async emitOk() {
            this.errorMessage = null;
            this.$v.name.$touch();
            this.$v.description.$touch();
            if (this.$v.$anyError) {
                return;
            }

            this.isLoading = true;
            try {
                await http.post('/api/rest/v1/mediation/ABTest', {
                    testType: testTypeQuick,
                    appKey: this.appKey,
                    name: this.name,
                    description: this.description
                });
                if (isAngular()) {
                    getAngularProvider('abTestConfigService')
                        .clearCache();
                }
                this.$emit('ok');
            } catch (error) {
                this.errorMessage = error?.response?.data?.error || 'We had a problem, please try again.';
            }
            this.isLoading = false;
        }
    },
    validations: {
        name: {
            required,
            minLength: minLength(1),
            maxLength: maxLength(nameMaxLength)
        },
        description: {
            maxLength: maxLength(80)
        }
    }
};
</script>

<style lang="scss" scoped>
@import "~styles/styleGuide";
@import "~styles/input.scss";

.start-quick-modal.v1 ::v-deep .modal-container,
.start-quick-modal.v2 ::v-deep .modal-container {
    width: 600px;
}

.start-quick-modal.v1 ::v-deep .loader-inline{
    position: unset;
    margin-left: -7px;
    margin-right: 5px;
    margin-top: -1px;
}

.start-quick-modal ::v-deep .modal-header {
    padding: 0;
}

.start-quick-modal ::v-deep .close-button-container {
    height: 0;
}

.start-quick-modal ::v-deep .modal-body {
    padding-left: 25px;
}

.start-quick-modal ::v-deep .app-name {
    max-width: 520px;
}

.modal-title {
    color: $dark-500;
    border-bottom: $border;
    padding: 15px 25px;
}

.modal-header {
    width: 100%;
}

.app-header {
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.23);
    padding-left: 22px;
    padding-right: 30px;
}

.field {
    display: grid;
    grid-template-columns: 200px auto;
    margin-bottom: 15px;

    label {
        margin-top: 5px;
    }
}

.optional {
    color: $light-700;
}

.check {
    color: $light-900;
    margin-bottom: 10px;
}

.check-text {
    margin-left: 3px;
}

.body {
    margin-top: 20px;
}

.description {
    height: 100px;
    margin-bottom: 15px;
    line-height: 1.5;
}

.error-message {
    color: $negative;
    line-height: 20px;
    grid-column-start: 2;
    margin-top: 5px;
}

.platform-text {
    color: $light-800;
}

.create-error-message {
    margin-top: 0px;
    margin-bottom: 10px;
}
</style>
